import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Row, SortingState } from '@tanstack/react-table';
import { Spinner } from '@cmsgov/design-system';
import {
  cancelScheduledRemoval,
  exportContractRoster,
  removeRosterMember,
  useApprovalsQuery,
  useCertifyContractRoster,
  useContractRosterQuery,
  useResendContractRosterInvitation,
} from '@access-cms/api-client';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';

import Table, { TableControls, TableFooter } from '../../components/Table';
import TableResultSummary from '../../components/TableResultSummary';
import { useAlerts, useDebounce, useModal } from '../../hooks';
import {
  type AddRosterUserData,
  AddRosterUserModal,
  ContractFormActions,
  PendingRemoval,
  RemoveUserModal,
  RosterChange,
  RosterChangeType,
  RosterExpandedRow,
  RosterInstructions,
  RosterTableActions,
  UnsavedRosterChangesModal,
  UpdateRosterModal,
} from './components';
import { useContractData } from './context';
import { useRosterColumns } from './hooks';
import type { RosterEntry } from './types';
import { downloadBlob } from './utils/rosterUtils';
import { formatDate, getTableMinHeight, getTableMinWidth, ModalSize } from '../../utils';
import ConfirmActionModal from './components/ConfirmActionModal/ConfirmActionModal';

import { CONTRACT_ROLE, useRBAC } from '@/hooks/useRBAC';

import './Contract.css';

const ROSTER_FILTER_OPTIONS = [
  { label: 'Cleared', value: 'cleared' },
  { label: 'In Progress', value: 'inProgress' },
  { label: 'Non-U.S. Citizen', value: 'nonUsCitizen' },
  { label: 'Not Vetted', value: 'notVetted' },
  { label: 'Six Months or Less', value: 'sixMonthsOrLess' },
  { label: 'U.S. Citizen', value: 'usCitizen' },
];
const CITIZENSHIP_FILTERS = ['usCitizen', 'nonUsCitizen'];
const ROSTER_SORT_FIELDS = {
  laborCategory: 'labor_category',
  legalFirstName: 'legal_first_name',
  legalLastName: 'legal_last_name',
} as const;
type RosterSortField = keyof typeof ROSTER_SORT_FIELDS;
type RosterSortOrder = 'asc' | 'desc';

function getRosterSortField(sort: string | null): RosterSortField | undefined {
  return Object.entries(ROSTER_SORT_FIELDS).find(([, value]) => value === sort)?.[0] as RosterSortField | undefined;
}

type RosterFilter = 'cleared' | 'inProgress' | 'nonUsCitizen' | 'notVetted' | 'sixMonthsOrLess' | 'usCitizen';

function ContractRosterTab() {
  const { contractId, contract } = useContractData();
  const { openModal, closeModal } = useModal();
  const { dismissAlert, errorAlert, successAlert } = useAlerts();
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('keyword') ?? '';
  const selectedFilters = searchParams
    .getAll('filter')
    .filter((value): value is RosterFilter => ROSTER_FILTER_OPTIONS.some((option) => option.value === value));
  const debouncedKeyword = useDebounce(keyword, 300);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isCancellingRemoval, setIsCancellingRemoval] = useState(false);
  const [cancelRemovalEntry, setCancelRemovalEntry] = useState<RosterEntry | null>(null);
  const [pendingRemovals, setPendingRemovals] = useState<Map<string, PendingRemoval>>(new Map());
  const rosterLoadErrorAlertId = useRef<string | null>(null);
  const previousContractIdRef = useRef<string | null>(contractId);
  const baseTestId = 'contract-roster-tab';
  const pageParam = Number(searchParams.get('page'));
  const limitParam = Number(searchParams.get('limit'));
  const currentPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const recordsPerPage = [5, 10, 25, 50, 100].includes(limitParam) ? limitParam : 10;
  const sortField = getRosterSortField(searchParams.get('sort'));
  const sortOrder: RosterSortOrder = searchParams.get('order') === 'desc' ? 'desc' : 'asc';
  const sortBy: SortingState = sortField ? [{ desc: sortOrder === 'desc', id: sortField }] : [];
  const updateRosterSearchParams = useCallback(
    (updates: {
      filters?: string[];
      keyword?: string;
      limit?: number;
      order?: RosterSortOrder | null;
      page?: number;
      sort?: RosterSortField | null;
    }) => {
      setSearchParams(
        (previousParams) => {
          const nextParams = new URLSearchParams(previousParams);

          if (updates.keyword !== undefined) {
            if (updates.keyword) nextParams.set('keyword', updates.keyword);
            else nextParams.delete('keyword');
          }

          if (updates.filters !== undefined) {
            nextParams.delete('filter');
            updates.filters.forEach((filter) => nextParams.append('filter', filter));
          }

          if (updates.page !== undefined) {
            if (updates.page === 1) nextParams.delete('page');
            else nextParams.set('page', String(updates.page));
          }

          if (updates.limit !== undefined) {
            if (updates.limit === 10) nextParams.delete('limit');
            else nextParams.set('limit', String(updates.limit));
          }

          if (updates.sort !== undefined) {
            if (updates.sort) nextParams.set('sort', ROSTER_SORT_FIELDS[updates.sort]);
            else nextParams.delete('sort');
          }

          if (updates.order !== undefined) {
            if (updates.order) nextParams.set('order', updates.order);
            else nextParams.delete('order');
          }

          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const handleSortingChange = useCallback(
    (nextSorting: SortingState) => {
      const nextSort = nextSorting[0];

      if (!nextSort || !Object.hasOwn(ROSTER_SORT_FIELDS, nextSort.id)) {
        updateRosterSearchParams({ order: null, page: 1, sort: null });

        return;
      }

      updateRosterSearchParams({
        order: nextSort.desc ? 'desc' : 'asc',
        page: 1,
        sort: nextSort.id as RosterSortField,
      });
    },
    [updateRosterSearchParams],
  );
  const { hasRoleOnContract, isAdmin } = useRBAC();
  const isRosterOwner = hasRoleOnContract(contractId ?? '', CONTRACT_ROLE.ROSTER_OWNER);
  const isCor = hasRoleOnContract(contractId ?? '', CONTRACT_ROLE.COR);
  const canManageRoster = isAdmin || isRosterOwner;
  const canViewRoster = canManageRoster || isCor;
  const rosterSearchFilters = useMemo(() => {
    const isUsCitizen: 'true' | 'false' | undefined = selectedFilters.includes('usCitizen')
      ? 'true'
      : selectedFilters.includes('nonUsCitizen')
      ? 'false'
      : undefined;

    return {
      ...(debouncedKeyword.trim() ? { keyword: debouncedKeyword.trim() } : {}),
      ...(isUsCitizen ? { isUsCitizen } : {}),
      ...(selectedFilters.includes('sixMonthsOrLess') ? { isSeasonal: 'true' as const } : {}),
    };
  }, [debouncedKeyword, selectedFilters]);
  const hasServerFilter = Object.keys(rosterSearchFilters).length > 0;

  const rosterQuery = useContractRosterQuery(contractId || '', {
    enabled: !!contractId,
    filters: {
      ...rosterSearchFilters,
      limit: recordsPerPage,
      ...(sortField ? { order: sortOrder, sort: ROSTER_SORT_FIELDS[sortField] } : {}),
      page: currentPage,
    },
  });
  const { isPending: isResendInvitationPending, mutate: resendInvitation } = useResendContractRosterInvitation(
    contractId || '',
  );
  const certifyRoster = useCertifyContractRoster(contractId || '');
  const navigate = useNavigate();

  // The approvals endpoint is assignment-aware, so only surface COR reviews
  // that the current user is actually allowed to action.
  const assignedContractApprovals = useApprovalsQuery({
    enabled: !!contractId && isCor,
    filters: { contractId: contractId || '', limit: 100, stage: 'COR' },
  });

  const pendingApprovals = useMemo(() => {
    const map = new Map<string, string>();
    if (!assignedContractApprovals.data?.data) return map;

    for (const approval of assignedContractApprovals.data.data) {
      if (approval.applicant_user_id && approval.application_id) {
        map.set(approval.applicant_user_id, approval.application_id);
      }
    }

    return map;
  }, [assignedContractApprovals.data]);

  const handleReviewApplication = useCallback(
    (applicationId: string) => {
      navigate(`/approvals/application/${applicationId}`);
    },
    [navigate],
  );

  interface EnrichedRosterMember {
    userId?: string;
    dateAdded?: string;
    rosterStatus?: string;
    isSeasonal?: boolean;
    scheduledRemovalDate?: string | null;
    vettingCompletedAt?: string | null;
    userProfile?: {
      email?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      isUsCitizen?: boolean | null;
    } | null;
    laborCategoryDetails?: {
      name?: string;
      description?: string | null;
      riskLevel?: string | null;
    } | null;
  }

  const rosterData: RosterEntry[] = useMemo(() => {
    if (rosterQuery.isError) return [];
    if (!rosterQuery.data?.data) return [];

    return rosterQuery.data.data.map((member) => {
      const enrichedMember = member as unknown as EnrichedRosterMember;
      const userProfile = enrichedMember.userProfile;
      const laborCategoryDetails = enrichedMember.laborCategoryDetails;

      return {
        email: userProfile?.email || '',
        isUSCitizen: userProfile?.isUsCitizen ?? null,
        laborCategory: laborCategoryDetails?.name || '',
        legalFirstName: userProfile?.firstName || '',
        legalLastName: userProfile?.lastName || '',
        riskLevel: laborCategoryDetails?.riskLevel || '',
        scheduledRemovalDate: enrichedMember.scheduledRemovalDate,
        sixMonthsOrLess: enrichedMember.isSeasonal || false,
        userId: enrichedMember.userId || '',
        vettingEndDate: formatDate(enrichedMember.vettingCompletedAt),
        vettingProgress: enrichedMember.rosterStatus || '',
        vettingStartDate: formatDate(enrichedMember.dateAdded),
      };
    });
  }, [rosterQuery.data, rosterQuery.isError]);

  const pendingChanges = useMemo(() => {
    const changes: RosterChange[] = [];
    pendingRemovals.forEach((removal) => {
      const user = rosterData.find((u) => u.userId === removal.userId);
      if (user) {
        changes.push({
          date: removal.removalDate,
          type: RosterChangeType.REMOVE,
          userName: `${user.legalFirstName} ${user.legalLastName}`,
        });
      }
    });

    return changes;
  }, [pendingRemovals, rosterData]);

  const hasChanges = pendingChanges.length > 0;

  // ACP-2183: scheduling a removal only stages it. Nothing reaches the contract until
  // Update Roster is clicked, and leaving before that threw the work away in silence —
  // the behaviour behind INC1980606, INC1980267 and INC1979005.
  //
  // The guard covers every in-app route change, not only this page's own buttons: the
  // header links and the browser back button are where the work actually got lost.
  // Closing or reloading the tab is handled separately below, because the browser owns
  // that dialog and will not show our wording.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return hasChanges && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (!hasChanges) return undefined;

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Set for the browsers that still read it; the text shown is the browser's own.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warnBeforeUnload);

    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasChanges]);

  // `blocker` is a fresh object on every render, so keying the effect on it would reopen
  // the modal in a loop. The effect runs on the state transition and reads the current
  // blocker through a ref, which is the one that still has a live proceed/reset.
  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  // Assigned below, once certifyPendingChanges exists. The modal is created imperatively,
  // so it never re-renders — every handler it is given has to read through a ref.
  const certifyRef = useRef<(() => Promise<boolean>) | null>(null);
  const isCertifyingRef = useRef(false);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    openModal(
      <UnsavedRosterChangesModal
        changes={pendingChangesRef.current}
        onCancel={() => {
          closeModal();
          blockerRef.current.reset?.();
        }}
        onCertify={async () => {
          // The dialog stays open while the save runs, so a second click is reachable.
          if (isCertifyingRef.current) return;
          isCertifyingRef.current = true;

          const saved = await certifyRef.current?.();

          isCertifyingRef.current = false;

          // Only release the navigation once the work is safely stored. On a failure the
          // dialog stays open behind the error alert so the user can retry or discard.
          if (saved) blockerRef.current.proceed?.();
        }}
        onLeave={() => {
          closeModal();
          blockerRef.current.proceed?.();
        }}
      />,
      'This page has unsaved changes',
      ModalSize.Large,
    );
    // Only the transition into "blocked" should open it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);

  const isSearchSettled = keyword === debouncedKeyword;
  const isResultSettled = isSearchSettled && !rosterQuery.isFetching && !rosterQuery.isError;
  const paginationMeta = rosterQuery.data?.meta?.pagination;
  const totalRecords = paginationMeta?.total ?? rosterData.length;
  const totalPages = paginationMeta?.totalPages ?? Math.ceil(totalRecords / recordsPerPage);
  useEffect(() => {
    if (previousContractIdRef.current && contractId && previousContractIdRef.current !== contractId) {
      updateRosterSearchParams({ page: 1 });
    }

    previousContractIdRef.current = contractId;
  }, [contractId, updateRosterSearchParams]);

  useEffect(() => {
    if (!rosterQuery.isError) {
      if (rosterLoadErrorAlertId.current) {
        dismissAlert(rosterLoadErrorAlertId.current);
        rosterLoadErrorAlertId.current = null;
      }

      return;
    }

    if (!rosterLoadErrorAlertId.current) {
      rosterLoadErrorAlertId.current = errorAlert({
        message: "Sorry, we couldn't load the contract roster. Please try again.",
        title: 'Unable to load roster',
      });
    }
  }, [dismissAlert, errorAlert, rosterQuery.isError]);

  useEffect(() => {
    return () => {
      if (rosterLoadErrorAlertId.current) {
        dismissAlert(rosterLoadErrorAlertId.current);
      }
    };
  }, [dismissAlert]);

  const handleRemoveUserClick = useCallback(
    (entry: RosterEntry) => {
      if (!canManageRoster) return;

      const handleConfirmRemoval = (userId: string, removalDate: string) => {
        setPendingRemovals((prev) => {
          const newMap = new Map(prev);
          newMap.set(userId, { removalDate, userId });

          return newMap;
        });
        closeModal();
      };

      openModal(
        <RemoveUserModal
          user={entry}
          onCancel={closeModal}
          onConfirm={handleConfirmRemoval}
        />,
        'Remove User from Roster',
        ModalSize.Medium,
        // The calendar floats outside the modal frame; without this it is clipped.
        'modal-content--overflow-visible',
      );
    },
    [canManageRoster, openModal, closeModal],
  );

  const handleCancelRemoval = useCallback(
    (entry: RosterEntry) => {
      if (!canManageRoster) return;
      setCancelRemovalEntry(entry);
    },
    [canManageRoster],
  );

  const handleConfirmCancellation = useCallback(async () => {
    const entry = cancelRemovalEntry;
    if (!entry || isCancellingRemoval) return;

    if (pendingRemovals.has(entry.userId)) {
      setPendingRemovals((prev) => {
        const next = new Map(prev);
        next.delete(entry.userId);

        return next;
      });

      return;
    }

    if (!contractId || !entry.scheduledRemovalDate) return;

    setIsCancellingRemoval(true);
    try {
      await cancelScheduledRemoval(contractId, entry.userId);
      await rosterQuery.refetch();
      successAlert({
        dismissInSeconds: 30,
        message: `The scheduled removal for ${entry.legalFirstName} ${entry.legalLastName} has been canceled.`,
        title: 'Scheduled removal canceled',
      });
    } catch {
      errorAlert({
        message: `We couldn't cancel the scheduled removal for ${entry.legalFirstName} ${entry.legalLastName}. Please try again.`,
        title: 'Unable to cancel scheduled removal',
      });
    } finally {
      setIsCancellingRemoval(false);
    }
  }, [cancelRemovalEntry, isCancellingRemoval, pendingRemovals, contractId, rosterQuery, successAlert, errorAlert]);

  const removalDate = cancelRemovalEntry
    ? pendingRemovals.get(cancelRemovalEntry.userId)?.removalDate ?? formatDate(cancelRemovalEntry.scheduledRemovalDate)
    : '';

  const handleResendInvitation = useCallback(
    (entry: RosterEntry) => {
      if (!canManageRoster || isResendInvitationPending) {
        return;
      }

      const userName = `${entry.legalFirstName} ${entry.legalLastName}`.trim() || entry.email;

      resendInvitation(
        { userId: entry.userId },
        {
          onError: () => {
            errorAlert({
              message: `We couldn't resend the invitation to ${userName}. Please try again.`,
              title: 'Unable to resend invitation',
            });
          },
          onSuccess: () => {
            successAlert({
              dismissInSeconds: 30,
              message: `A new invitation has been sent to ${userName}.`,
              title: 'Invitation resent successfully',
            });
          },
        },
      );
    },
    [canManageRoster, errorAlert, isResendInvitationPending, resendInvitation, successAlert],
  );

  // ACP-2183: HCD put a "Certify Roster" button inside the unsaved-changes warning, so the
  // save can no longer live in handleUpdateRoster's closure. It reports whether it landed,
  // because a caller that is holding a navigation must not release it on a failure.
  const certifyPendingChanges = useCallback(async (): Promise<boolean> => {
    const removals = Array.from(pendingRemovals.values());

    if (!contractId) return false;

    try {
      if (removals.length > 0) {
        await Promise.all(
          removals.map((removal) => {
            const [month, day, year] = removal.removalDate.split('/');
            const scheduledRemovalDate = `${year}-${month}-${day}`;

            return removeRosterMember(contractId, removal.userId, { scheduledRemovalDate });
          }),
        );
      }

      const nextRosterUpdateDueDate = (await certifyRoster.mutateAsync({})).data?.nextRosterUpdateDueDate;

      setPendingRemovals(new Map());
      closeModal();
      await rosterQuery.refetch();
      successAlert({
        dismissInSeconds: 30,
        message: `Next Roster Update due on ${formatDate(nextRosterUpdateDueDate)}.`,
        title: 'Roster has been successfully certified.',
      });

      return true;
    } catch {
      errorAlert({
        message: "We couldn't certify the roster. Please try again.",
        title: 'Unable to certify roster',
      });

      return false;
    }
  }, [certifyRoster, closeModal, contractId, errorAlert, pendingRemovals, rosterQuery, successAlert]);

  certifyRef.current = certifyPendingChanges;

  const handleUpdateRoster = useCallback(() => {
    if (!canManageRoster) return;

    const handleDiscardChanges = () => {
      setPendingRemovals(new Map());
      closeModal();
    };

    const handleConfirmUpdate = certifyPendingChanges;

    if (hasChanges) {
      openModal(
        <UpdateRosterModal
          changes={pendingChanges}
          onCancel={closeModal}
          onDiscardChanges={handleDiscardChanges}
          onUpdateRoster={handleConfirmUpdate}
        />,
        'Are you sure you want to update the Roster?',
        ModalSize.Large,
      );
    } else {
      openModal(
        <UpdateRosterModal
          changes={[]}
          onCancel={closeModal}
          onDiscardChanges={closeModal}
          onUpdateRoster={handleConfirmUpdate}
        />,
        'Are you sure you want to certify the Roster?',
        ModalSize.Large,
      );
    }
  }, [canManageRoster, certifyPendingChanges, closeModal, hasChanges, openModal, pendingChanges]);

  const handleAddUser = useCallback(() => {
    if (!canManageRoster) return;

    const handleCancel = () => {
      closeModal();
    };

    const handleRequestError = (message: string) => {
      closeModal();
      errorAlert({
        message,
        title: 'Error',
      });
    };

    const handleSuccess = async (userData: AddRosterUserData) => {
      try {
        const result = await rosterQuery.refetch();
        if (result.error) {
          closeModal();
          errorAlert({
            message: 'Unable to refresh roster. Please try again.',
            title: 'Unable to refresh roster',
          });

          return;
        }

        closeModal();
        successAlert({
          dismissInSeconds: 30,
          message: `${userData.firstName} ${userData.lastName} has been added to the roster.`,
          title: 'Success',
        });
      } catch {
        closeModal();
        errorAlert({
          message: 'Unable to refresh roster. Please try again.',
          title: 'Unable to refresh roster',
        });
      }
    };

    openModal(
      <AddRosterUserModal
        contractId={contractId || ''}
        contractNumber={contract?.contractNumber}
        hasTemporarySeasonalUsers={contract?.hasTemporarySeasonalUsers}
        onCancel={handleCancel}
        onRequestError={handleRequestError}
        onSuccess={handleSuccess}
      />,
      'Add User to Roster',
      ModalSize.Fit,
    );
  }, [
    canManageRoster,
    openModal,
    closeModal,
    contractId,
    contract?.contractNumber,
    contract?.hasTemporarySeasonalUsers,
    rosterQuery,
    errorAlert,
    successAlert,
  ]);

  /**
   * Download the roster as CSV.
   *
   * The file is generated server-side so untrusted values cannot be evaluated
   * as spreadsheet formulas [ACP-2142]. That also means the export covers the
   * whole matching roster rather than the page currently on screen.
   */
  const handleExportRoster = useCallback(async () => {
    if (!contractId || isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const { blob, filename, headers } = hasServerFilter
        ? await exportContractRoster(contractId, rosterSearchFilters)
        : await exportContractRoster(contractId);

      downloadBlob(blob, filename ?? `roster-export-${timestamp}.csv`);

      // A saved file cannot warn about itself, so say it here when the server
      // capped the export.
      if (headers.get('X-Roster-Export-Truncated') === 'true') {
        errorAlert({
          message: `This roster is too large to export in full. The file contains the first ${headers.get(
            'X-Roster-Export-Row-Count',
          )} members only.`,
          title: 'Export is incomplete',
        });
      }
    } catch (error) {
      console.error('Failed to export roster.', error);
      errorAlert({
        message: "Sorry, we couldn't export the roster. Please try again.",
        title: 'Unable to export roster',
      });
    } finally {
      setIsExporting(false);
    }
  }, [contractId, errorAlert, hasServerFilter, isExporting, rosterSearchFilters]);

  const handleSearchChange = useCallback(
    (value: string) => {
      updateRosterSearchParams({ keyword: value, page: 1 });
    },
    [updateRosterSearchParams],
  );

  const handleFiltersApply = useCallback(
    (filters: string[]) => {
      updateRosterSearchParams({ filters, page: 1 });
    },
    [updateRosterSearchParams],
  );

  const handleRecordsPerPageChange = useCallback(
    (value: number) => {
      updateRosterSearchParams({ limit: value, page: 1 });
    },
    [updateRosterSearchParams],
  );

  const recordsPerPageOptions = useMemo(
    () =>
      [5, 10, 25, 50, 100].map((value) => ({
        label: `${value} users per page`,
        value: value.toString(),
      })),
    [],
  );

  const getRowId = useCallback((row: RosterEntry) => row.userId || row.email, []);

  const handleExpandAll = useCallback(() => {
    const allExpanded = rosterData.length > 0 && rosterData.every((row) => expanded[getRowId(row)]);

    if (allExpanded) {
      setExpanded({});
    } else {
      setExpanded(
        rosterData.reduce<Record<string, boolean>>((nextExpanded, row) => {
          const rowId = getRowId(row);
          if (rowId) {
            nextExpanded[rowId] = true;
          }

          return nextExpanded;
        }, {}),
      );
    }
  }, [rosterData, expanded, getRowId]);

  const expandAllLabel = useMemo(() => {
    const allExpanded = rosterData.length > 0 && rosterData.every((row) => expanded[getRowId(row)]);

    return allExpanded ? 'Collapse All' : 'Expand All';
  }, [rosterData, expanded, getRowId]);

  const columns = useRosterColumns({
    canManageRoster,
    isCor,
    isResendInvitationPending,
    onCancelRemoval: handleCancelRemoval,
    onRemoveUser: handleRemoveUserClick,
    onResendInvitation: handleResendInvitation,
    onReviewApplication: handleReviewApplication,
    pendingApprovals,
    pendingRemovals,
  });

  const tableMinHeight = getTableMinHeight(recordsPerPage, 64);
  const tableMinWidth = getTableMinWidth(columns);

  const renderExpandedRow = (row: Row<RosterEntry>) => {
    const data = row.original;

    return (
      <RosterExpandedRow
        contractId={contractId || ''}
        data={data}
        rowIndex={row.index}
      />
    );
  };

  if (!contractId) {
    return (
      <div
        className='contract-roster-tab-content'
        data-testid={baseTestId}>
        <p
          className='ds-text'
          data-testid={`${baseTestId}-empty`}>
          No contract selected.
        </p>
      </div>
    );
  }

  if (!canViewRoster) {
    return (
      <div
        className='contract-roster-tab-content'
        data-testid={baseTestId}
      />
    );
  }

  if (rosterQuery.isError) {
    return (
      <div
        className='contract-roster-tab-content'
        data-testid={baseTestId}
      />
    );
  }

  return (
    <div
      className='contract-roster-tab-content'
      data-testid={baseTestId}>
      <div data-testid={`${baseTestId}-instructions`}>
        <RosterInstructions />
      </div>

      <div
        className='access-table-shell contract-roster__table-container'
        data-testid={`${baseTestId}-table-container`}>
        <TableControls
          className='contract-roster__table-toolbar'
          data-testid={`${baseTestId}-controls`}
          exclusiveFilterValues={CITIZENSHIP_FILTERS}
          expandCollapseLabel={expandAllLabel}
          filterOptions={ROSTER_FILTER_OPTIONS}
          resultCount={isResultSettled ? totalRecords : undefined}
          searchInput={keyword}
          searchPlaceholder='Search...'
          selectedFilters={selectedFilters}
          showExpandCollapse={rosterData.length > 0}
          onExpandCollapse={handleExpandAll}
          onFiltersApply={handleFiltersApply}
          onSearchChange={handleSearchChange}
        />

        <div
          className='access-table-wrapper contract-roster__table-wrapper'
          data-testid={`${baseTestId}-table`}>
          <div style={{ minHeight: `${tableMinHeight}px`, minWidth: `${tableMinWidth}px` }}>
            {rosterQuery.isLoading ? (
              <div
                className='access-table-status'
                data-testid={`${baseTestId}-loading`}>
                <div className='access-table-status__card'>
                  <Spinner aria-valuetext='Loading roster...' />
                </div>
              </div>
            ) : rosterData.length === 0 ? (
              <div
                className='access-table-status'
                data-testid={`${baseTestId}-empty-state`}>
                <div className='access-table-status__card'>
                  <p
                    className='access-table-status__message'
                    data-testid={`${baseTestId}-empty-message`}>
                    {hasServerFilter
                      ? 'No roster entries match your search or filter criteria.'
                      : 'No users on this roster yet.'}
                  </p>
                  {hasServerFilter && (
                    <p
                      className='access-table-status__hint'
                      data-testid={`${baseTestId}-empty-hint`}>
                      Try adjusting your search terms or filters.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <Table
                enableSorting
                manualSorting
                ariaLabel='Contract roster table'
                caption={`Contract roster table showing ${totalRecords} entries`}
                className='contract-roster__table'
                columns={columns}
                data={rosterData}
                data-testid={`${baseTestId}-table-component`}
                expanded={expanded}
                getRowId={getRowId}
                renderExpandedRow={renderExpandedRow}
                sortBy={sortBy}
                onExpandedChange={setExpanded}
                onSortingChange={handleSortingChange}
              />
            )}
          </div>
        </div>

        {!rosterQuery.isLoading && rosterData.length > 0 && (
          <TableFooter
            className='contract-roster__table-footer'
            currentPage={currentPage}
            data-testid={`${baseTestId}-footer`}
            recordsDropdownName='contract-roster-records-per-page'
            recordsPerPage={recordsPerPage}
            recordsPerPageOptions={recordsPerPageOptions}
            totalPages={totalPages}
            onPageChange={(page) => updateRosterSearchParams({ page })}
            onRecordsPerPageChange={handleRecordsPerPageChange}
          />
        )}
      </div>
      {!rosterQuery.isLoading && rosterData.length > 0 && (
        <TableResultSummary
          data-testid={`${baseTestId}-summary`}
          pagination={paginationMeta}
          rowCount={rosterData.length}
        />
      )}

      {!rosterQuery.isLoading && (
        <div className='contract-roster__actions'>
          <ContractFormActions
            data-testid={`${baseTestId}-back-actions`}
            isEditable={false}
          />
          <RosterTableActions
            canManageRoster={canManageRoster}
            data-testid={`${baseTestId}-roster-actions`}
            hasChanges={hasChanges}
            isExporting={isExporting}
            onAddUser={handleAddUser}
            onExportRoster={handleExportRoster}
            onUpdateRoster={handleUpdateRoster}
          />
        </div>
      )}
      <ConfirmActionModal
        cancelLabel='No'
        confirmLabel='Yes'
        data-testid='cancel-scheduled-removal-modal'
        description={
          <>
            Are you sure you want to cancel the removal of{' '}
            <strong>
              {cancelRemovalEntry?.legalFirstName} {cancelRemovalEntry?.legalLastName}
            </strong>{' '}
            scheduled for <strong>{removalDate}</strong>? They will remain on the contract until removed at a later time
            or when the contract expires.
          </>
        }
        isConfirming={isCancellingRemoval}
        isOpen={cancelRemovalEntry !== null}
        title='Cancel Scheduled Removal?'
        variant='rounded'
        onClose={() => setCancelRemovalEntry(null)}
        onConfirm={handleConfirmCancellation}
      />
    </div>
  );
}

export default ContractRosterTab;
